import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";

const CURRENCIES = ["INR", "USD", "CAD", "AED"];
const SYM = { INR: "\u20b9", USD: "$", CAD: "C$", AED: "AED " };
const nowMonth = () => new Date().toISOString().slice(0, 7);
const monthRange = (month) => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start: `${month}-01`,
    end: `${month}-${String(last).padStart(2, "0")}`,
  };
};
const money = (v, cur = "INR") =>
  `${SYM[cur] || ""}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_FLOW = ["draft", "review", "approved", "locked", "paid"];
const NEXT_LABEL = {
  draft: "Send to review",
  review: "Approve",
  approved: "Lock",
  locked: "Mark paid",
};

export default function PayrollWorkspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = ["super_admin", "founder", "administrator"].includes(
    user?.role,
  );
  const canCommission = [
    "super_admin",
    "founder",
    "administrator",
    "hr",
    "hr_admin",
  ].includes(user?.role);
  const [tab, setTab] = useState("runs");
  const [entities, setEntities] = useState([]);
  const [runs, setRuns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statutory, setStatutory] = useState([]);
  const [loans, setLoans] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [eligiblePlacements, setEligiblePlacements] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runCreateOpen, setRunCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ent, rn, emp, stat, ln, adj, cm, ep] = await Promise.all([
        api.get("/hrms/payroll/entities"),
        api.get("/hrms/payroll/runs"),
        api.get("/hrms/payroll/eligible-employees"),
        api.get("/hrms/payroll/statutory-rules"),
        api.get("/hrms/payroll/loans"),
        api.get("/hrms/payroll/adjustments"),
        api.get("/hrms/commissions").catch(() => ({ data: { items: [] } })),
        api
          .get("/hrms/commissions/eligible-placements")
          .catch(() => ({ data: { items: [] } })),
      ]);
      setEntities(ent.data.items || []);
      setRuns(rn.data.items || []);
      setEmployees(emp.data.items || []);
      setStatutory(stat.data.items || []);
      setLoans(ln.data.items || []);
      setAdjustments(adj.data.items || []);
      setCommissions(cm.data.items || []);
      setEligiblePlacements(ep.data.items || []);
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openRun = async (id) => {
    try {
      const { data } = await api.get(`/hrms/payroll/runs/${id}`);
      setSelectedRun(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (selectedRun) {
    return (
      <RunDetail
        detail={selectedRun}
        entities={entities}
        isSuper={isSuper}
        onBack={() => setSelectedRun(null)}
        onRefresh={async () => {
          await load();
          await openRun(selectedRun.run.id);
        }}
      />
    );
  }

  const tabs = [
    ["runs", "Payroll runs"],
    ["salary", "Salary structures"],
    ...(isSuper ? [["entities", "Legal entities"]] : []),
    ["statutory", "Statutory rules"],
    ["loans", "Loans & adjustments"],
    ...(canCommission ? [["commissions", "Commissions"]] : []),
    ["fnf", "Full & final"],
  ];

  return (
    <div className="space-y-6" data-testid="payroll-workspace">
      <PageHeader
        title="Payroll workspace"
        subtitle="Salary structures, statutory rules, multi-currency runs, payslips and settlements."
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/hrms")}
            data-testid="payroll-back-hrms"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to HRMS
          </Button>
        }
      />
      <div
        className="flex flex-wrap gap-2 border-b border-border pb-3"
        data-testid="payroll-tabs"
      >
        {tabs.map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={tab === value ? "default" : "outline"}
            onClick={() => setTab(value)}
            data-testid={`payroll-tab-${value}`}
            className={tab === value ? "bg-[#255058] hover:bg-[#1c3d43]" : ""}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "runs" && (
        <RunsTab
          runs={runs}
          onOpen={openRun}
          onNew={() => setRunCreateOpen(true)}
        />
      )}
      {tab === "salary" && <SalaryTab employees={employees} onRefresh={load} />}
      {tab === "entities" && (
        <LegalEntitiesTab entities={entities} onRefresh={load} />
      )}
      {tab === "statutory" && (
        <StatutoryTab rules={statutory} onRefresh={load} />
      )}
      {tab === "loans" && (
        <LoansTab
          loans={loans}
          adjustments={adjustments}
          employees={employees}
          onRefresh={load}
        />
      )}
      {tab === "commissions" && (
        <CommissionsTab
          commissions={commissions}
          eligiblePlacements={eligiblePlacements}
          isSuper={isSuper}
          onRefresh={load}
        />
      )}
      {tab === "fnf" && <FnFTab employees={employees} />}

      <RunCreateDialog
        open={runCreateOpen}
        entities={entities}
        onClose={() => setRunCreateOpen(false)}
        onCreated={async (id) => {
          setRunCreateOpen(false);
          await load();
          await openRun(id);
        }}
      />
    </div>
  );
}

function LegalEntitiesTab({ entities, onRefresh }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden" data-testid="legal-entities-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entity</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Tax country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entities.map((entity) => (
                <TableRow
                  key={entity.id}
                  data-testid={`legal-entity-row-${entity.id}`}
                >
                  <TableCell>
                    <div className="font-medium">{entity.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {entity.legal_name}
                    </div>
                  </TableCell>
                  <TableCell>{entity.default_currency}</TableCell>
                  <TableCell>{entity.default_statutory_country}</TableCell>
                  <TableCell>
                    <StatusBadge status={entity.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(entity)}
                      data-testid={`legal-entity-edit-${entity.id}`}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      {editing && (
        <LegalEntityDialog
          entity={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

function LegalEntityDialog({ entity, onClose, onSaved }) {
  const [form, setForm] = useState(entity);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/hrms/payroll/entities/${entity.id}`, {
        name: form.name,
        legal_name: form.legal_name || "",
        address: form.address || "",
        tax_id: form.tax_id || "",
        brand_color: form.brand_color || "#255058",
        default_currency: form.default_currency || "INR",
        default_statutory_country: form.default_statutory_country || "none",
        status: form.status || "active",
      });
      toast.success("Legal entity updated");
      onSaved();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  const change = (key, value) => setForm({ ...form, [key]: value });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="legal-entity-edit-dialog"
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Edit legal entity</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Display name</Label>
            <Input
              value={form.name}
              onChange={(event) => change("name", event.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Legal name</Label>
            <Input
              value={form.legal_name || ""}
              onChange={(event) => change("legal_name", event.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Address</Label>
            <Textarea
              value={form.address || ""}
              onChange={(event) => change("address", event.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Tax ID</Label>
            <Input
              value={form.tax_id || ""}
              onChange={(event) => change("tax_id", event.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Brand color</Label>
            <Input
              value={form.brand_color || "#255058"}
              onChange={(event) => change("brand_color", event.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Default currency</Label>
            <Select
              value={form.default_currency || "INR"}
              onValueChange={(value) => change("default_currency", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Statutory country</Label>
            <Select
              value={form.default_statutory_country || "none"}
              onValueChange={(value) =>
                change("default_statutory_country", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="IN">India</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select
              value={form.status || "active"}
              onValueChange={(value) => change("status", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="legal-entity-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : "Save entity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold">{value}</div>
    </Card>
  );
}

/* --------------------------------- Runs --------------------------------- */
function RunsTab({ runs, onOpen, onNew }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={onNew}
          data-testid="payroll-new-run-button"
          className="bg-[#255058] text-white hover:bg-[#1c3d43]"
        >
          <Plus className="mr-1.5 h-4 w-4" /> New payroll run
        </Button>
      </div>
      <Card className="overflow-hidden" data-testid="payroll-runs-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Employees</TableHead>
                <TableHead>Gross (INR)</TableHead>
                <TableHead>Net (INR)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No payroll runs yet. Create your first run.
                  </TableCell>
                </TableRow>
              )}
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`payroll-run-row-${r.id}`}>
                  <TableCell className="font-medium">
                    {r.period_start} → {r.period_end}
                  </TableCell>
                  <TableCell>{r.entity_name}</TableCell>
                  <TableCell>{r.totals?.count ?? 0}</TableCell>
                  <TableCell>{money(r.totals?.gross_base)}</TableCell>
                  <TableCell>{money(r.totals?.net_base)}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpen(r.id)}
                      data-testid={`payroll-open-run-${r.id}`}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function RunCreateDialog({ open, entities, onClose, onCreated }) {
  const [month, setMonth] = useState(nowMonth());
  const [entity, setEntity] = useState("");
  const [prorate, setProrate] = useState(false);
  const [rates, setRates] = useState({ USD: 83.5, CAD: 61, AED: 22.7 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && entities.length && !entity) setEntity(entities[0].code);
  }, [open, entities, entity]);

  const submit = async () => {
    if (!entity) return toast.error("Choose a legal entity");
    const { start, end } = monthRange(month);
    setSaving(true);
    try {
      const { data } = await api.post("/hrms/payroll/runs", {
        period_start: start,
        period_end: end,
        entity,
        prorate,
        exchange_rates: rates,
      });
      toast.success("Draft payroll run created");
      onCreated(data.id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="payroll-run-create-dialog">
        <DialogHeader>
          <DialogTitle>New payroll run</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Payroll month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="payroll-run-month"
            />
          </div>
          <div>
            <Label className="text-xs">Legal entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="payroll-run-entity">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => (
                  <SelectItem key={e.code} value={e.code}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Exchange rates to INR (base)</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {["USD", "CAD", "AED"].map((c) => (
                <div key={c}>
                  <Label className="text-[10px] text-muted-foreground">
                    {c}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={rates[c]}
                    onChange={(e) =>
                      setRates({ ...rates, [c]: Number(e.target.value) })
                    }
                    data-testid={`payroll-rate-${c}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <Button
            type="button"
            variant={prorate ? "default" : "outline"}
            onClick={() => setProrate(!prorate)}
            data-testid="payroll-run-prorate"
            className={
              prorate
                ? "w-full bg-[#255058] text-white hover:bg-[#1c3d43]"
                : "w-full"
            }
          >
            Pro-rate by attendance (LOP): {prorate ? "On" : "Off"}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="payroll-run-create-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunDetail({ detail, entities, isSuper, onBack, onRefresh }) {
  const run = detail.run;
  const payslips = detail.payslips || [];
  const [busy, setBusy] = useState(false);
  const [editSlip, setEditSlip] = useState(null);
  const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(run.status) + 1];
  const frozen = ["locked", "paid"].includes(run.status);

  const transition = async (to, reason = "") => {
    setBusy(true);
    try {
      await api.post(`/hrms/payroll/runs/${run.id}/transition`, { to, reason });
      toast.success(`Run moved to ${to}`);
      await onRefresh();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  const recalc = async () => {
    setBusy(true);
    try {
      await api.post(`/hrms/payroll/runs/${run.id}/recalculate`);
      toast.success("Recalculated");
      await onRefresh();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  const bulkEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hrms/payroll/runs/${run.id}/email`);
      toast.success(
        `Emailed ${data.sent} payslip(s)${data.failed ? `, ${data.failed} failed` : ""}`,
      );
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  const download = async (kind, slip) => {
    try {
      const url =
        kind === "pdf"
          ? `/hrms/payroll/runs/${run.id}/payslips/${slip.id}/pdf`
          : `/hrms/payroll/runs/${run.id}/export/${kind}`;
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download =
        kind === "pdf"
          ? `payslip_${slip.employee_name}.pdf`
          : `${kind.replace("/", "_")}_${run.month}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="payroll-run-detail">
      <PageHeader
        title={`${run.entity_name} — ${run.period_start} to ${run.period_end}`}
        subtitle={`Status: ${run.status.toUpperCase()} · ${payslips.length} payslips`}
        actions={
          <Button
            variant="outline"
            onClick={onBack}
            data-testid="payroll-run-back"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" /> All runs
          </Button>
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Employees" value={run.totals?.count ?? 0} />
        <Metric label="Gross (INR)" value={money(run.totals?.gross_base)} />
        <Metric label="Net (INR)" value={money(run.totals?.net_base)} />
        <Metric label="Status" value={<StatusBadge status={run.status} />} />
      </div>

      <Card
        className="flex flex-wrap items-center gap-2 p-4"
        data-testid="payroll-run-actions"
      >
        {!frozen && (
          <Button
            size="sm"
            variant="outline"
            onClick={recalc}
            disabled={busy}
            data-testid="payroll-run-recalc"
          >
            Re-run calculation
          </Button>
        )}
        {nextStatus && (
          <Button
            size="sm"
            onClick={() => transition(nextStatus)}
            disabled={busy}
            data-testid="payroll-run-advance"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {NEXT_LABEL[run.status]}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={bulkEmail}
          disabled={busy}
          data-testid="payroll-run-bulk-email"
        >
          Email all payslips
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => download("export/bank-csv", null)}
          data-testid="payroll-export-bank"
        >
          Bank CSV
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => download("export/register", null)}
          data-testid="payroll-export-register"
        >
          Register
        </Button>
        {["pf", "esi", "pt", "tds"].map((c) => (
          <Button
            key={c}
            size="sm"
            variant="outline"
            onClick={() => download(`export/statutory/${c}`, null)}
            data-testid={`payroll-export-${c}`}
          >
            {c.toUpperCase()}
          </Button>
        ))}
      </Card>

      <Card className="overflow-hidden" data-testid="payroll-payslip-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Cur</TableHead>
                <TableHead>Present/Leave/LOP</TableHead>
                <TableHead>Gross</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Net</TableHead>
                <TableHead>Net (INR)</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((p) => (
                <TableRow key={p.id} data-testid={`payslip-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.employee_name}
                    {p.manually_edited && (
                      <span className="ml-1 text-[10px] text-red-500">
                        edited
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{p.currency}</TableCell>
                  <TableCell className="text-xs">
                    {p.present_days}/{p.approved_leave_days}/{p.lop_days}
                  </TableCell>
                  <TableCell>{money(p.gross_pay, p.currency)}</TableCell>
                  <TableCell>{money(p.total_deductions, p.currency)}</TableCell>
                  <TableCell className="font-semibold">
                    {money(p.net_pay, p.currency)}
                  </TableCell>
                  <TableCell>{money(p.net_base)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => download("pdf", p)}
                      data-testid={`payslip-pdf-${p.id}`}
                    >
                      PDF
                    </Button>
                    {isSuper && frozen && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-1"
                        onClick={() => setEditSlip(p)}
                        data-testid={`payslip-edit-${p.id}`}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {editSlip && (
        <PayslipEditDialog
          runId={run.id}
          slip={editSlip}
          onClose={() => setEditSlip(null)}
          onSaved={async () => {
            setEditSlip(null);
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

function PayslipEditDialog({ runId, slip, onClose, onSaved }) {
  const [earnings, setEarnings] = useState(slip.earning_lines || []);
  const [deductions, setDeductions] = useState(slip.deduction_lines || []);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const setAmount = (list, setList, i, v) => {
    const next = [...list];
    next[i] = { ...next[i], amount: Number(v) };
    setList(next);
  };
  const submit = async () => {
    if (reason.trim().length < 3) return toast.error("A reason is required");
    setSaving(true);
    try {
      await api.patch(`/hrms/payroll/runs/${runId}/payslips/${slip.id}`, {
        reason,
        earning_lines: earnings,
        deduction_lines: deductions,
      });
      toast.success("Payslip adjusted and regenerated");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="payslip-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Super Admin override — {slip.employee_name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-3 overflow-y-auto">
          <div className="text-xs font-semibold text-muted-foreground">
            Earnings
          </div>
          {earnings.map((l, i) => (
            <div key={`e${i}`} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{l.label}</span>
              <Input
                type="number"
                className="w-32"
                value={l.amount}
                onChange={(e) =>
                  setAmount(earnings, setEarnings, i, e.target.value)
                }
              />
            </div>
          ))}
          <div className="text-xs font-semibold text-muted-foreground">
            Deductions
          </div>
          {deductions.map((l, i) => (
            <div key={`d${i}`} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{l.label}</span>
              <Input
                type="number"
                className="w-32"
                value={l.amount}
                onChange={(e) =>
                  setAmount(deductions, setDeductions, i, e.target.value)
                }
              />
            </div>
          ))}
          <div>
            <Label className="text-xs">Reason (required, audit-logged)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="payslip-edit-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="payslip-edit-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------- Salary structures --------------------------- */
function SalaryTab({ employees, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);

  const view = async (emp) => {
    setSelected(emp);
    try {
      const { data } = await api.get(`/hrms/payroll/structures/${emp.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card className="p-3" data-testid="salary-employee-list">
        <div className="mb-2 text-sm font-semibold">Employees</div>
        <div className="space-y-1">
          {employees.map((e) => (
            <button
              key={e.id}
              onClick={() => view(e)}
              data-testid={`salary-emp-${e.id}`}
              className={`w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted ${selected?.id === e.id ? "bg-muted" : ""}`}
            >
              <div className="font-medium">{e.name}</div>
              <div className="text-xs text-muted-foreground">
                {e.entity} ·{" "}
                {e.has_structure ? `${e.currency} ${e.basic}` : "No structure"}
              </div>
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-4" data-testid="salary-detail">
        {!detail && (
          <div className="text-sm text-muted-foreground">
            Select an employee to view their salary structure.
          </div>
        )}
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-semibold">
                {selected?.name}
              </div>
              <Button
                size="sm"
                onClick={() => setOpen(true)}
                data-testid="salary-add-revision"
                className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Plus className="mr-1.5 h-4 w-4" /> New revision
              </Button>
            </div>
            {detail.current ? (
              <div className="rounded-md border border-border p-3 text-sm">
                <div className="mb-2 font-semibold">
                  Current (from {detail.current.effective_from}) ·{" "}
                  {detail.current.currency} · {detail.current.statutory_country}
                </div>
                <div>
                  Basic: {money(detail.current.basic, detail.current.currency)}
                </div>
                {detail.current.earnings.map((c, i) => (
                  <div key={i}>
                    {c.label}:{" "}
                    {c.calc === "percent_of_basic"
                      ? `${c.value}% of basic`
                      : money(c.value, detail.current.currency)}
                    {!c.taxable && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (non-taxable)
                      </span>
                    )}
                  </div>
                ))}
                {detail.current.fixed_deductions.map((d, i) => (
                  <div key={i} className="text-red-600">
                    - {d.label}: {money(d.amount, detail.current.currency)}
                  </div>
                ))}
                <div className="mt-1 text-xs text-muted-foreground">
                  OT rate/hr:{" "}
                  {money(
                    detail.current.overtime_hourly_rate,
                    detail.current.currency,
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No salary structure yet.
              </div>
            )}
            {detail.history.length > 1 && (
              <div>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">
                  Salary history
                </div>
                {detail.history.map((h) => (
                  <div
                    key={h.id}
                    className="flex justify-between border-b border-border py-1 text-sm"
                  >
                    <span>
                      {h.effective_from}{" "}
                      {h.is_current && (
                        <span className="text-xs text-emerald-600">
                          (current)
                        </span>
                      )}
                    </span>
                    <span>{money(h.basic, h.currency)} basic</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
      {open && selected && (
        <SalaryRevisionDialog
          employee={selected}
          current={detail?.current}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await view(selected);
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

function SalaryRevisionDialog({ employee, current, onClose, onSaved }) {
  const [form, setForm] = useState({
    effective_from: nowMonth() + "-01",
    currency: current?.currency || "INR",
    statutory_country: current?.statutory_country || "none",
    basic: current?.basic || 0,
    overtime_hourly_rate: current?.overtime_hourly_rate || 0,
    earnings: current?.earnings || [],
    fixed_deductions: current?.fixed_deductions || [],
  });
  const [saving, setSaving] = useState(false);
  const addEarn = () =>
    setForm({
      ...form,
      earnings: [
        ...form.earnings,
        {
          code: "allowance",
          label: "",
          calc: "fixed",
          value: 0,
          taxable: true,
        },
      ],
    });
  const addDed = () =>
    setForm({
      ...form,
      fixed_deductions: [
        ...form.fixed_deductions,
        { code: "deduction", label: "", amount: 0 },
      ],
    });
  const upd = (key, i, field, v) => {
    const next = [...form[key]];
    next[i] = { ...next[i], [field]: v };
    setForm({ ...form, [key]: next });
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/hrms/payroll/structures/${employee.id}`, {
        ...form,
        basic: Number(form.basic),
        overtime_hourly_rate: Number(form.overtime_hourly_rate),
        earnings: form.earnings.map((e) => ({ ...e, value: Number(e.value) })),
        fixed_deductions: form.fixed_deductions.map((d) => ({
          ...d,
          amount: Number(d.amount),
        })),
      });
      toast.success("Salary revision saved");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="salary-revision-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New salary revision — {employee.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Effective from</Label>
              <Input
                type="date"
                value={form.effective_from}
                onChange={(e) =>
                  setForm({ ...form, effective_from: e.target.value })
                }
                data-testid="salary-effective-from"
              />
            </div>
            <div>
              <Label className="text-xs">Currency</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger data-testid="salary-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statutory country</Label>
              <Select
                value={form.statutory_country}
                onValueChange={(v) =>
                  setForm({ ...form, statutory_country: v })
                }
              >
                <SelectTrigger data-testid="salary-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN">India</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Basic (monthly)</Label>
              <Input
                type="number"
                value={form.basic}
                onChange={(e) => setForm({ ...form, basic: e.target.value })}
                data-testid="salary-basic"
              />
            </div>
            <div>
              <Label className="text-xs">Overtime rate / hour</Label>
              <Input
                type="number"
                value={form.overtime_hourly_rate}
                onChange={(e) =>
                  setForm({ ...form, overtime_hourly_rate: e.target.value })
                }
                data-testid="salary-ot-rate"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-muted-foreground">
              Earnings (HRA, allowances)
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={addEarn}
              data-testid="salary-add-earning"
            >
              Add
            </Button>
          </div>
          {form.earnings.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_100px] gap-2">
              <Input
                placeholder="Label"
                value={c.label}
                onChange={(e) => upd("earnings", i, "label", e.target.value)}
              />
              <Select
                value={c.calc}
                onValueChange={(v) => upd("earnings", i, "calc", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="percent_of_basic">% of basic</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={c.value}
                onChange={(e) => upd("earnings", i, "value", e.target.value)}
              />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-muted-foreground">
              Fixed deductions
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={addDed}
              data-testid="salary-add-deduction"
            >
              Add
            </Button>
          </div>
          {form.fixed_deductions.map((d, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px] gap-2">
              <Input
                placeholder="Label"
                value={d.label}
                onChange={(e) =>
                  upd("fixed_deductions", i, "label", e.target.value)
                }
              />
              <Input
                type="number"
                value={d.amount}
                onChange={(e) =>
                  upd("fixed_deductions", i, "amount", e.target.value)
                }
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="salary-revision-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : "Save revision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Statutory --------------------------------- */
function StatutoryTab({ rules, onRefresh }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden" data-testid="statutory-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead>Rule</TableHead>
                <TableHead>Configuration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.id} data-testid={`statutory-row-${r.code}`}>
                  <TableCell>{r.country}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="max-w-[360px] truncate text-xs text-muted-foreground">
                    {JSON.stringify(r.config)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(r)}
                      data-testid={`statutory-edit-${r.code}`}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      {editing && (
        <StatutoryEditDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

function StatutoryEditDialog({ rule, onClose, onSaved }) {
  const [name, setName] = useState(rule.name);
  const [config, setConfig] = useState(JSON.stringify(rule.config, null, 2));
  const [status, setStatus] = useState(rule.status);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    let parsed;
    try {
      parsed = JSON.parse(config);
    } catch {
      return toast.error("Configuration must be valid JSON");
    }
    setSaving(true);
    try {
      await api.put(`/hrms/payroll/statutory-rules/${rule.id}`, {
        name,
        config: parsed,
        status,
      });
      toast.success("Statutory rule updated");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="statutory-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {rule.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Configuration (JSON)</Label>
            <Textarea
              rows={10}
              className="font-mono text-xs"
              value={config}
              onChange={(e) => setConfig(e.target.value)}
              data-testid="statutory-config"
            />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="statutory-edit-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------- Loans & adjustments -------------------------- */
function LoansTab({ loans, adjustments, employees, onRefresh }) {
  const [loanForm, setLoanForm] = useState({
    employee_id: "",
    principal: 0,
    emi: 0,
    currency: "INR",
    note: "",
  });
  const [adjForm, setAdjForm] = useState({
    employee_id: "",
    month: nowMonth(),
    type: "arrear",
    label: "",
    amount: 0,
  });
  const empName = (id) => employees.find((e) => e.id === id)?.name || id;

  const addLoan = async () => {
    if (!loanForm.employee_id) return toast.error("Select an employee");
    try {
      await api.post("/hrms/payroll/loans", {
        ...loanForm,
        principal: Number(loanForm.principal),
        emi: Number(loanForm.emi),
      });
      toast.success("Loan added");
      setLoanForm({
        employee_id: "",
        principal: 0,
        emi: 0,
        currency: "INR",
        note: "",
      });
      await onRefresh();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const addAdj = async () => {
    if (!adjForm.employee_id || !adjForm.label)
      return toast.error("Employee and label required");
    try {
      await api.post("/hrms/payroll/adjustments", {
        ...adjForm,
        amount: Number(adjForm.amount),
      });
      toast.success("Adjustment added");
      setAdjForm({
        employee_id: "",
        month: nowMonth(),
        type: "arrear",
        label: "",
        amount: 0,
      });
      await onRefresh();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-4" data-testid="loans-card">
        <div className="font-display text-lg font-semibold">
          Loans & advances (EMI)
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Select
            value={loanForm.employee_id}
            onValueChange={(v) => setLoanForm({ ...loanForm, employee_id: v })}
          >
            <SelectTrigger data-testid="loan-employee">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={loanForm.currency}
            onValueChange={(v) => setLoanForm({ ...loanForm, currency: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            placeholder="Principal"
            value={loanForm.principal}
            onChange={(e) =>
              setLoanForm({ ...loanForm, principal: e.target.value })
            }
            data-testid="loan-principal"
          />
          <Input
            type="number"
            placeholder="EMI / month"
            value={loanForm.emi}
            onChange={(e) => setLoanForm({ ...loanForm, emi: e.target.value })}
            data-testid="loan-emi"
          />
        </div>
        <Button
          className="mt-2 bg-[#255058] text-white hover:bg-[#1c3d43]"
          size="sm"
          onClick={addLoan}
          data-testid="loan-add"
        >
          Add loan
        </Button>
        <div className="mt-3 space-y-1">
          {loans.map((l) => (
            <div
              key={l.id}
              className="flex justify-between border-b border-border py-1 text-sm"
            >
              <span>{empName(l.employee_id)}</span>
              <span>
                {money(l.balance, l.currency)} left · EMI{" "}
                {money(l.emi, l.currency)} · <StatusBadge status={l.status} />
              </span>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4" data-testid="adjustments-card">
        <div className="font-display text-lg font-semibold">
          One-time adjustments
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Select
            value={adjForm.employee_id}
            onValueChange={(v) => setAdjForm({ ...adjForm, employee_id: v })}
          >
            <SelectTrigger data-testid="adj-employee">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="month"
            value={adjForm.month}
            onChange={(e) => setAdjForm({ ...adjForm, month: e.target.value })}
            data-testid="adj-month"
          />
          <Select
            value={adjForm.type}
            onValueChange={(v) => setAdjForm({ ...adjForm, type: v })}
          >
            <SelectTrigger data-testid="adj-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="arrear">Arrear</SelectItem>
              <SelectItem value="bonus">Bonus</SelectItem>
              <SelectItem value="incentive">Incentive</SelectItem>
              <SelectItem value="reimbursement">Reimbursement</SelectItem>
              <SelectItem value="one_time">One-time (+/-)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            placeholder="Amount"
            value={adjForm.amount}
            onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })}
            data-testid="adj-amount"
          />
          <Input
            placeholder="Label"
            className="col-span-2"
            value={adjForm.label}
            onChange={(e) => setAdjForm({ ...adjForm, label: e.target.value })}
            data-testid="adj-label"
          />
        </div>
        <Button
          className="mt-2 bg-[#255058] text-white hover:bg-[#1c3d43]"
          size="sm"
          onClick={addAdj}
          data-testid="adj-add"
        >
          Add adjustment
        </Button>
        <div className="mt-3 space-y-1">
          {adjustments.map((a) => (
            <div
              key={a.id}
              className="flex justify-between border-b border-border py-1 text-sm"
            >
              <span>
                {empName(a.employee_id)} · {a.type} · {a.month}
              </span>
              <span>
                {a.label}: {money(a.amount)}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ----------------------------- Full & final ------------------------------ */
function FnFTab({ employees }) {
  const [form, setForm] = useState({
    employee_id: "",
    days_worked: 0,
    working_days: 26,
    unused_leave_days: 0,
    notice_recovery_days: 0,
    gratuity: 0,
    other_earnings: 0,
    other_deductions: 0,
  });
  const [result, setResult] = useState(null);
  const cur = useMemo(
    () => employees.find((e) => e.id === form.employee_id)?.currency || "INR",
    [employees, form.employee_id],
  );
  const calc = async () => {
    if (!form.employee_id) return toast.error("Select an employee");
    try {
      const { data } = await api.post("/hrms/payroll/full-and-final", {
        ...form,
        days_worked: Number(form.days_worked),
        working_days: Number(form.working_days),
        unused_leave_days: Number(form.unused_leave_days),
        notice_recovery_days: Number(form.notice_recovery_days),
        gratuity: Number(form.gratuity),
        other_earnings: Number(form.other_earnings),
        other_deductions: Number(form.other_deductions),
      });
      setResult(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const num = (key, label) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`fnf-${key}`}
      />
    </div>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-4" data-testid="fnf-form">
        <div className="font-display text-lg font-semibold">
          Full & final settlement
        </div>
        <div className="mt-3 space-y-3">
          <Select
            value={form.employee_id}
            onValueChange={(v) => setForm({ ...form, employee_id: v })}
          >
            <SelectTrigger data-testid="fnf-employee">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            {num("days_worked", "Days worked (last period)")}
            {num("working_days", "Working days in month")}
            {num("unused_leave_days", "Unused leave (encash)")}
            {num("notice_recovery_days", "Notice recovery days")}
            {num("gratuity", "Gratuity")}
            {num("other_earnings", "Other earnings")}
            {num("other_deductions", "Other deductions")}
          </div>
          <Button
            onClick={calc}
            data-testid="fnf-calc"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Calculate settlement
          </Button>
        </div>
      </Card>
      <Card className="p-4" data-testid="fnf-result">
        <div className="font-display text-lg font-semibold">Settlement</div>
        {!result && (
          <div className="mt-2 text-sm text-muted-foreground">
            Enter details and calculate.
          </div>
        )}
        {result && (
          <div className="mt-3 space-y-1 text-sm">
            <Row label="Last pay" value={money(result.last_pay, cur)} />
            <Row
              label="Leave encashment"
              value={money(result.leave_encashment, cur)}
            />
            <Row label="Gratuity" value={money(result.gratuity, cur)} />
            <Row
              label="Other earnings"
              value={money(result.other_earnings, cur)}
            />
            <Row
              label="Total earnings"
              value={money(result.total_earnings, cur)}
              bold
            />
            <Row
              label="Notice recovery"
              value={`- ${money(result.notice_recovery, cur)}`}
            />
            <Row
              label="Outstanding loan"
              value={`- ${money(result.outstanding_loan, cur)}`}
            />
            <Row
              label="Other deductions"
              value={`- ${money(result.other_deductions, cur)}`}
            />
            <Row
              label="Total deductions"
              value={`- ${money(result.total_deductions, cur)}`}
              bold
            />
            <div className="mt-2 rounded-md bg-[#255058] px-3 py-2 text-white">
              <div className="flex justify-between font-semibold">
                <span>Net settlement</span>
                <span data-testid="fnf-net">
                  {money(result.net_settlement, cur)}
                </span>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ----------------------------- Commissions ------------------------------ */
function CommissionsTab({
  commissions,
  eligiblePlacements,
  isSuper,
  onRefresh,
}) {
  const [raiseFor, setRaiseFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/hrms/commissions/${c.id}/statement/pdf`, {
        responseType: "blob",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = `commission_${c.candidate_name}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const cancel = async (c) => {
    try {
      await api.post(`/hrms/commissions/${c.id}/cancel`, {
        reason: "Cancelled by admin",
      });
      toast.success("Commission cancelled");
      await onRefresh();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const open = eligiblePlacements.filter((p) => !p.commission_id);
  const visibleCommissions = commissions.filter(
    (c) => c.status !== "cancelled",
  );

  return (
    <div className="space-y-5" data-testid="commissions-tab">
      <Card className="overflow-hidden" data-testid="commission-eligible-table">
        <div className="border-b border-border px-4 py-2 text-sm font-semibold">
          Placements without a commission
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Invoice paid</TableHead>
                <TableHead>Guarantee ended</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {open.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No placements pending a commission.
                  </TableCell>
                </TableRow>
              )}
              {open.map((p) => (
                <TableRow
                  key={p.placement_id}
                  data-testid={`commission-placement-${p.placement_id}`}
                >
                  <TableCell className="font-medium">
                    {p.candidate_name}
                  </TableCell>
                  <TableCell>{p.client_name}</TableCell>
                  <TableCell>{money(p.placement_fee, p.currency)}</TableCell>
                  <TableCell>
                    {p.eligibility?.invoice_paid ? "Yes" : "No"}
                  </TableCell>
                  <TableCell>
                    {p.eligibility?.guarantee_ended ? "Yes" : "No"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => setRaiseFor(p)}
                      data-testid={`commission-raise-${p.placement_id}`}
                      className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                    >
                      Raise commission
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="commission-list-table">
        <div className="border-b border-border px-4 py-2 text-sm font-semibold">
          Commissions
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placement</TableHead>
                <TableHead>Participants</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Eligible</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleCommissions.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No commissions yet.
                  </TableCell>
                </TableRow>
              )}
              {visibleCommissions.map((c) => (
                <TableRow key={c.id} data-testid={`commission-row-${c.id}`}>
                  <TableCell className="font-medium">
                    {c.candidate_name}
                    <div className="text-xs text-muted-foreground">
                      {c.client_name}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {(c.participants || [])
                      .map((p) => `${p.name} (${money(p.amount, c.currency)})`)
                      .join(", ")}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {money(c.total_amount, c.currency)}
                  </TableCell>
                  <TableCell>
                    {c.eligibility?.is_eligible ? "Yes" : "No"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={c.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {c.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => setApproveFor(c)}
                        data-testid={`commission-approve-${c.id}`}
                        className="mr-1 bg-[#255058] text-white hover:bg-[#1c3d43]"
                      >
                        Approve
                      </Button>
                    )}
                    {isSuper && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-1"
                        onClick={() => setEditFor(c)}
                        data-testid={`commission-edit-${c.id}`}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPdf(c)}
                      data-testid={`commission-pdf-${c.id}`}
                    >
                      PDF
                    </Button>
                    {c.status !== "cancelled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-1"
                        onClick={() => cancel(c)}
                        data-testid={`commission-cancel-${c.id}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {raiseFor && (
        <CommissionRaiseDialog
          placement={raiseFor}
          onClose={() => setRaiseFor(null)}
          onSaved={async () => {
            setRaiseFor(null);
            await onRefresh();
          }}
        />
      )}
      {editFor && (
        <CommissionRaiseDialog
          commission={editFor}
          placement={editFor}
          onClose={() => setEditFor(null)}
          onSaved={async () => {
            setEditFor(null);
            await onRefresh();
          }}
        />
      )}
      {approveFor && (
        <CommissionApproveDialog
          commission={approveFor}
          isSuper={isSuper}
          onClose={() => setApproveFor(null)}
          onSaved={async () => {
            setApproveFor(null);
            await onRefresh();
          }}
        />
      )}
    </div>
  );
}

function CommissionRaiseDialog({ commission, placement, onClose, onSaved }) {
  const [participants, setParticipants] = useState(() => {
    if (commission) return commission.participants || [];
    const seed = [];
    if (placement.recruiter_id)
      seed.push({
        user_id: placement.recruiter_id,
        name: placement.recruiter_name || "Recruiter",
        role: "recruiter",
        basis: "percent",
        value: 7,
      });
    if (placement.bdm_id)
      seed.push({
        user_id: placement.bdm_id,
        name: "BDM",
        role: "bdm",
        basis: "percent",
        value: 3,
      });
    return seed.length
      ? seed
      : [
          {
            user_id: "",
            name: "",
            role: "recruiter",
            basis: "percent",
            value: 0,
          },
        ];
  });
  const [notes, setNotes] = useState(commission?.notes || "");
  const [saving, setSaving] = useState(false);
  const upd = (i, field, v) => {
    const n = [...participants];
    n[i] = { ...n[i], [field]: v };
    setParticipants(n);
  };
  const add = () =>
    setParticipants([
      ...participants,
      {
        user_id: "",
        name: "",
        role: "account_manager",
        basis: "percent",
        value: 0,
      },
    ]);
  const remove = (i) =>
    setParticipants(participants.filter((_, idx) => idx !== i));
  const amount = (p) =>
    p.basis === "percent"
      ? (placement.placement_fee * Number(p.value || 0)) / 100
      : Number(p.value || 0);
  const total = participants.reduce((s, p) => s + amount(p), 0);
  const submit = async () => {
    const valid = participants.filter((p) => p.user_id && p.name);
    if (!valid.length)
      return toast.error("Add at least one participant with a name and user");
    setSaving(true);
    try {
      const payload = {
        participants: valid.map((participant) => ({
          ...participant,
          value: Number(participant.value),
        })),
        notes,
      };
      if (commission) {
        await api.put(`/hrms/commissions/${commission.id}`, payload);
        toast.success("Commission updated");
      } else {
        await api.post("/hrms/commissions", {
          ...payload,
          placement_id: placement.placement_id,
        });
        toast.success("Commission raised (draft)");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="commission-raise-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {commission ? "Edit commission" : "Raise commission"} —{" "}
            {placement.candidate_name}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto">
          <div className="text-sm text-muted-foreground">
            Fee: {money(placement.placement_fee, placement.currency)} · split
            across participants (% of fee or fixed).
          </div>
          {participants.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_110px_100px_90px_32px] items-center gap-2"
            >
              <Input
                placeholder="Name"
                value={p.name}
                onChange={(e) => upd(i, "name", e.target.value)}
                data-testid={`commission-part-name-${i}`}
              />
              <Select value={p.role} onValueChange={(v) => upd(i, "role", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                  <SelectItem value="bdm">BDM</SelectItem>
                  <SelectItem value="account_manager">Account Mgr</SelectItem>
                  <SelectItem value="ra">RA</SelectItem>
                </SelectContent>
              </Select>
              <Select value={p.basis} onValueChange={(v) => upd(i, "basis", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">% of fee</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={p.value}
                onChange={(e) => upd(i, "value", e.target.value)}
                data-testid={`commission-part-value-${i}`}
              />
              <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                ×
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            data-testid="commission-add-participant"
          >
            Add participant
          </Button>
          <div className="flex justify-between rounded-md bg-muted px-3 py-2 text-sm font-semibold">
            <span>Total commission</span>
            <span data-testid="commission-total-preview">
              {money(total, placement.currency)}
            </span>
          </div>
          <Textarea
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="commission-raise-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving
              ? "Saving…"
              : commission
                ? "Save commission"
                : "Raise commission"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommissionApproveDialog({ commission, isSuper, onClose, onSaved }) {
  const eligible = commission.eligibility?.is_eligible;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!eligible && !reason.trim())
      return toast.error(
        "This commission is not eligible yet — a Super Admin reason is required to override",
      );
    setSaving(true);
    try {
      await api.post(`/hrms/commissions/${commission.id}/approve`, { reason });
      toast.success("Commission approved and queued to payroll");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="commission-approve-dialog">
        <DialogHeader>
          <DialogTitle>
            Approve commission — {commission.candidate_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            Total:{" "}
            <strong>
              {money(commission.total_amount, commission.currency)}
            </strong>
          </div>
          <div className={eligible ? "text-emerald-600" : "text-amber-600"}>
            {eligible
              ? "Eligible — invoice paid and guarantee ended."
              : `Not eligible yet (invoice paid: ${commission.eligibility?.invoice_paid ? "yes" : "no"}, guarantee ended: ${commission.eligibility?.guarantee_ended ? "yes" : "no"}).${isSuper ? " As Super Admin you may override with a reason." : ""}`}
          </div>
          {!eligible && (
            <div>
              <Label className="text-xs">
                Override reason {isSuper ? "(required)" : ""}
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="commission-approve-reason"
              />
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            On approval, each participant's share is added as an incentive to
            their next payroll run.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="commission-approve-submit"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold }) {
  return (
    <div
      className={`flex justify-between border-b border-border py-1 ${bold ? "font-semibold" : ""}`}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
